/**
 * Extraherar batterikapacitet (kWh) ur Car.info-specifikationer så att den kan
 * slås upp automatiskt i fordonets laddkonfiguration.
 *
 * Föredrar användbar/netto-kapacitet (det som faktiskt laddas) framför brutto,
 * eftersom laddredovisningen bygger på SoC × användbar kapacitet.
 *
 * Obs: Car.info lägger ofta EV-kWh under nestlade `children` (t.ex. under "El").
 */

interface SpecAttributeLike {
  name?: string;
  values?: unknown;
  unit?: string;
  children?: SpecAttributeLike[] | Record<string, SpecAttributeLike>;
}

interface SpecCategoryLike {
  attributes?: Record<string, SpecAttributeLike>;
}

interface CarInfoLike {
  specifications?: SpecCategoryLike[] | null;
  basicAttributes?: SpecAttributeLike[] | null;
  extendedAttributes?: SpecAttributeLike[] | null;
}

function parseKwh(values: unknown): number | null {
  const raw = Array.isArray(values) ? values.join(' ') : String(values ?? '');
  // Plocka första talet (stöder "77.4", "77,4", "77.4 kWh", "El: 77,4")
  const match = raw.replace(',', '.').match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const n = Number(match[0]);
  if (!Number.isFinite(n) || n <= 0 || n > 500) return null; // rimlighetskontroll
  return n;
}

function isBatteryCapacityName(name: string): boolean {
  const n = name.toLowerCase();
  return (
    n.includes('batterikapacitet') ||
    (n.includes('batteri') && n.includes('kapacitet')) ||
    (n.includes('battery') && n.includes('capacity'))
  );
}

/** Prioritet: användbar/netto > brutto/total > okänt. */
function capacityRank(name: string): number {
  const n = name.toLowerCase();
  if (n.includes('netto') || n.includes('användbar') || n.includes('usable') || n.includes('net')) {
    return 0;
  }
  if (n.includes('brutto') || n.includes('gross') || n.includes('total')) return 1;
  return 2;
}

function collectAttrs(
  node: SpecAttributeLike | SpecAttributeLike[] | Record<string, SpecAttributeLike> | null | undefined,
  out: SpecAttributeLike[]
): void {
  if (node == null) return;
  if (Array.isArray(node)) {
    for (const item of node) collectAttrs(item, out);
    return;
  }
  if (typeof node !== 'object') return;
  if (typeof node.name === 'string' || node.values != null) {
    out.push(node);
  }
  if (node.children) collectAttrs(node.children, out);
  for (const [key, val] of Object.entries(node as Record<string, unknown>)) {
    if (key === 'children' || key === 'name' || key === 'values' || key === 'unit' || key === 'id') continue;
    if (val && typeof val === 'object') {
      collectAttrs(val as SpecAttributeLike, out);
    }
  }
}

/**
 * Returnerar bästa kandidat för batterikapacitet (kWh) från Car.info, eller null.
 */
export function extractBatteryCapacityKwh(carInfo: CarInfoLike | null | undefined): number | null {
  if (!carInfo) return null;

  const attrs: SpecAttributeLike[] = [];
  for (const cat of carInfo.specifications ?? []) {
    collectAttrs(cat.attributes, attrs);
  }
  collectAttrs(carInfo.basicAttributes, attrs);
  collectAttrs(carInfo.extendedAttributes, attrs);

  const candidates: Array<{ rank: number; value: number }> = [];
  for (const a of attrs) {
    const name = (a?.name ?? '').trim();
    if (!name || !isBatteryCapacityName(name)) continue;
    // Kräv kWh — filtrerar bort 12V Ah-batteri
    if (!a.unit || !/kwh/i.test(a.unit)) continue;
    const value = parseKwh(a.values);
    if (value === null) continue;
    candidates.push({ rank: capacityRank(name), value });
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.rank - b.rank);
  return candidates[0].value;
}
